/* @flow */
// Phone and tablet layout: one Studio area with three tabs, Ads, Classes and
// Quotes, so each has its own screen instead of three panels stacked down the
// page. While an ad is being created or reviewed, or the timetable is open,
// the tabs step aside and that screen has the whole area to itself.
import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import AdsPanel from '../AdsPanel'
import ClassesPanel from '../ClassesPanel'
import MotivationPanel from '../MotivationPanel'
import { MegaphoneIcon, CalendarIcon, QuoteIcon } from '../AdsPanel/shared'
import './StudioPanel.css'

// key, tab label, icon, headline shown above the tabs (same words the panels used)
const TABS = [
  ['ads', 'Ads', MegaphoneIcon, 'Ads'],
  ['classes', 'Classes', CalendarIcon, 'Class announcements'],
  ['quotes', 'Quotes', QuoteIcon, 'Motivational quotes']
]

class StudioPanel extends Component {
  state = { tab: 'ads' }

  componentDidMount () {
    // The Classes badge needs the timetable even while the Ads tab is showing
    this.props.studioStore.loadClasses()
  }

  componentDidUpdate () {
    const { studioStore } = this.props
    // Opening a draft from somewhere else (e.g. the ads list) lands on the Ads tab
    if (studioStore.screen !== 'list' && this.state.tab !== 'ads') this.setState({ tab: 'ads' })
    if (studioStore.classesScreen === 'timetable' && this.state.tab !== 'classes') this.setState({ tab: 'classes' })
  }

  badge (key) {
    const { studioStore, audioMessageStore, userStore } = this.props
    if (key === 'ads') {
      const all = audioMessageStore.audioMessages || []
      const on = all.filter((m) => m.enabled).length
      return on ? `${on} on` : ''
    }
    if (key === 'classes') {
      const c = studioStore.classes
      if (!c || !c.approved || !(c.classes || []).length) return ''
      return c.enabled ? 'on' : 'off'
    }
    if (key === 'quotes') {
      return userStore.disableMotivationQuotes ? 'off' : 'on'
    }
    return ''
  }

  render () {
    const { studioStore, onToggleMessage, onSubscribe, onPreviewStart, onToggleMotivation } = this.props
    const { tab } = this.state
    const inFlow = studioStore.screen !== 'list' || studioStore.classesScreen === 'timetable'
    const current = TABS.find((t) => t[0] === tab) || TABS[0]
    const HeadIcon = current[2]

    return (
      <div className={ `studio-tabs ${inFlow ? 'studio-tabs--flow' : 'studio-tabs--titled'}` }>
        {
          !inFlow && (
            // The headline sits above the tabs, level with the "Stations" heading in the
            // left column on laptops; the panels' own headline is hidden meanwhile
            <header className="studio__header studio-tabs__headline">
              <span className="studio__icon"><HeadIcon /></span>
              <h2 className="studio__headline">{ current[3] }</h2>
            </header>
          )
        }
        {
          !inFlow && (
            <div className="studio-tabs__bar" role="tablist" aria-label="Studio">
              {
                TABS.map(([key, label, Icon]) => {
                  const badge = this.badge(key)
                  return (
                    <button
                      key={ key }
                      type="button"
                      role="tab"
                      aria-selected={ tab === key }
                      className={ `studio-tabs__tab ${tab === key ? 'is-active' : ''}` }
                      onClick={ () => this.setState({ tab: key }) }
                    >
                      <Icon />
                      <span className="studio-tabs__label">{ label }</span>
                      { badge && <span className="studio-tabs__badge">{ badge }</span> }
                    </button>
                  )
                })
              }
            </div>
          )
        }
        <div className="studio-tabs__body" role="tabpanel">
          { tab === 'ads' && <AdsPanel onToggleMessage={ onToggleMessage } onSubscribe={ onSubscribe } onPreviewStart={ onPreviewStart } /> }
          { tab === 'classes' && <ClassesPanel onSubscribe={ onSubscribe } onPreviewStart={ onPreviewStart } /> }
          { tab === 'quotes' && <MotivationPanel onToggleMotivation={ onToggleMotivation } /> }
        </div>
      </div>
    )
  }
}

export default inject((stores) => ({
  studioStore: stores.studioStore,
  audioMessageStore: stores.audioMessageStore,
  userStore: stores.userStore
}))(observer(StudioPanel))
